import PredefinedPeriod from 'src/models/reservation/workflow/PredefinedPeriod'
import PriceConfiguration from 'src/models/pricing/PriceConfiguration'
import { PeriodType } from 'src/models/period/enums/PeriodType'
import PeriodConfigurationJson from './interfaces/PeriodConfigurationJson'
import PeriodSelector from './PeriodSelector'
import CodelocksPeriodSelector from './CodelocksPeriodSelector'
import LockerType from '../../locker/LockerType'
import { DateTime } from 'luxon'
import PeriodTranslation from 'src/models/period/interfaces/PeriodTranslation'
import StaticTranslatable from 'src/models/period/Translation/StaticTranslatable'
import DynamicTranslatable from 'src/models/period/Translation/DynamicTranslatable'
import DynamicTranslation from 'src/models/period/Translation/DynamicTranslation'

export default class PeriodConfiguration {
  periodType: PeriodType
  periodSelector: PeriodSelector | null
  codelocksPeriodSelector: CodelocksPeriodSelector | null
  predefinedPeriods: Array<PredefinedPeriod>
  priceConfiguration: PriceConfiguration
  skipDurationIfOnlyOnePeriod: boolean

  constructor(
    periodType: PeriodType,
    periodSelector: PeriodSelector | null,
    codelocksPeriodSelector: CodelocksPeriodSelector | null,
    predefinedPeriods: Array<PredefinedPeriod>,
    priceConfiguration: PriceConfiguration,
    skipDurationIfOnlyOnePeriod: boolean,
  ) {
    this.periodType = periodType
    this.periodSelector = periodSelector
    this.codelocksPeriodSelector = codelocksPeriodSelector
    this.predefinedPeriods = predefinedPeriods
    this.priceConfiguration = priceConfiguration
    this.skipDurationIfOnlyOnePeriod = skipDurationIfOnlyOnePeriod
  }

  hasOnlyOneDurationChoice(_now: DateTime) {
    return (
      this.periodType === PeriodType.PREDEFINED &&
      this.predefinedPeriods.length === 1
    )
  }

  getPeriodTranslations(_now: DateTime): Array<PeriodTranslation> {
    if (this.periodType === PeriodType.PREDEFINED) {
      return this.predefinedPeriods.map(
        (period) =>
          new DynamicTranslatable(
            period.id.toString(),
            Object.fromEntries(
              Object.entries(period.translations).map(([locale, t]) => [
                locale,
                new DynamicTranslation(t.locale, t.label),
              ]),
            ),
          ),
      )
    } else if (this.periodType === PeriodType.PERIOD_SELECTOR) {
      return [
        new StaticTranslatable('week', 'reservation.pricing_table.week_price'),
        new StaticTranslatable('day', 'reservation.pricing_table.day_price'),
        new StaticTranslatable('hour', 'reservation.pricing_table.hour_price'),
      ]
    } else if (this.periodType === PeriodType.CODELOCKS_PERIOD_SELECTOR) {
      return [
        new StaticTranslatable('day', 'reservation.pricing_table.day_price'),
        new StaticTranslatable('hour', 'reservation.pricing_table.hour_price'),
      ]
    }

    throw new Error('Not implemented period type')
  }

  getPriceItemsPerLockerType(lockerType: LockerType) {
    return this.priceConfiguration.pricingTable.filter(
      (priceItem) => priceItem.lockerTypeId === lockerType.id,
    )
  }

  static fromJson(data: PeriodConfigurationJson) {
    return new this(
      data.periodType,
      data.periodSelector ? PeriodSelector.fromJson(data.periodSelector) : null,
      data.codelocksPeriodSelector
        ? CodelocksPeriodSelector.fromJson(data.codelocksPeriodSelector)
        : null,
      data.predefinedPeriods.map((item) => PredefinedPeriod.fromJson(item)),
      PriceConfiguration.fromJson(data.priceConfiguration),
      data.skipDurationIfOnlyOnePeriod,
    )
  }
}
